<template>
  <div>
    <RsFormInput
      :id="id"
      v-model="originalValue"
      class="mb-0"
      :type="editableValueFormType"
      :placeholder="placeholder"
      :disabled="inputDisabled"
      :style="customStyle"
      :variant="validationMessage ? 'error' : undefined"
      :custom-error-message="validationMessage"
      :debounce="500"
      :rules="rules"
      @input="handleEnter($event)"
      @enter="handleEnter($event.target.value)"
      @blur="handleEnter($event.target.value)"
    />

    <RsTooltip
      v-if="tooltipTitle"
      :target="id"
      :title="tooltipTitle"
      position="bottom"
    />

    <slot name="underField" />
  </div>
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import validationMixin from '@/mixins/validation-mixin';

export type TRsDataTableCellEditableChangeEventData = {
  data: string,
  validationMessage: string,
};

type TData = {
  originalValue: string;
  validationMessage: string;
};

export default defineComponent({
  name: 'RsCellEditable',

  mixins: [ validationMixin ],

  props: {
    rules: {
      type: Object,
      default: () => {},
    },
    id: {
      type: String,
      default: '',
    },
    tooltipTitle: {
      type: String,
      default: '',
    },
    value: {
      type: String,
      default: '',
      required: false,
    },
    /**
     * Valid RsFormInput values for 'rules' prop
     * number | email | password | search | url | tel | time | range | color | phone
     */
    editableValueFormRule: {
      type: String,
      default: '',
    },
    /**
     * Valid RsFormInput values for 'type' prop
     * text | number | email | password | search | url | tel | time | range | color
     */
    editableValueFormType: {
      type: String,
      default: 'text',
    },
    placeholder: {
      type: String,
      default: '',
    },
    inputDisabled: {
      type: Boolean,
      default: false,
    },
    customStyle: {
      type: String,
      default: '',
    },
  },

  emits: ['change'],
  data(): TData {
    return {
      originalValue: '',
      validationMessage: '',
    };
  },
  watch: {
    /**
     * Reports user edits only. `originalValue` is written from two directions: by the user
     * (RsFormInput's v-model) and by the `value` watcher below when the parent pushes a new
     * value down. Emitting on the latter echoes the parent's own value straight back at it —
     * and before the first prop sync lands it echoes the empty initial state. Parents that
     * rebuild their edit copy from every `change` event (e.g. CustomTracking's
     * `handleInfoChange`) then write that empty payload over the loaded value, and the echo
     * of the blanked prop lands after the real value, so saved data disappears from the row.
     */
    originalValue(newValue: string) {
      if (newValue === this.value) return;
      this.handleEnter(newValue);
    },
    value: {
      immediate: true,
      handler() {
        this.originalValue = this.value;
        // Keep an already-invalid stored value flagged on load, which the suppressed
        // load-time emit used to take care of via handleEnter().
        this.validationMessage = this.inputValidator(this.value)?.message || '';
      },
    },
  },

  mounted() {
    this.originalValue = this.value;
  },
  methods: {
    handleEnter(data: string): void {
      this.validationMessage = this.inputValidator(data)?.message || '';
      this.$emit('change', {
        data,
        validationMessage: this.validationMessage,
      });
    },

    inputValidator(value: string): { message: string } | null {
      let validation: { message: string } | null = null;
      if (!value) return null;
      switch (this.editableValueFormRule) {
        case 'phone':
          validation = !this.isPhone(`${value}`) ? { message: this.$ts('validation.phoneShouldBeValid') } : null;
          break;
        case 'email':
          validation = !this.isEmail(`${value}`) ? { message: this.$ts('validation.emailInvalid') } : null;
          break;
        case 'url':
          validation = !this.isURL(`${value}`) ? { message: this.$ts('validation.urlInvalid') } : null;
          break;
        default:
          validation = null;
      }
      return validation;
    },
  },
});
</script>
<style lang="scss">
@import "@/styles/_variables";
.rs-editable-text {
  &__link {
    font-style: $header-font-family;
    color: $primary-color;
  }
}
.rs-editable-text--info-color {
  color: $info;
}
</style>
